import { Fragment, useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Collapse, Badge } from "react-bootstrap";
import { Modal } from "react-bootstrap";
/// Link
import { Link, useNavigate } from "react-router-dom";
import { getMenuList, getFranchiseMenuList } from "./Menu";
import foodServing from "../../../assets/images/food-serving.png";
import { useScrollPosition } from "@n8tb1t/use-scroll-position";
import { useTranslation } from "react-i18next";
import { getCallsByRestaurant } from "../../../services/CallsService";
import { fetchConnectedUser } from "../../../services/OrdersService";
import { fetchBackofficeSidebarSettings } from "../../../services/appSettingsService";
import { filterMenuListByDisabledKeys } from "../../../utils/filterMenuList";
import { withLinkedArchiveKeys } from "../../../utils/backofficeMenuAccess";
import socket, { joinRestaurantRoom } from "../../../services/socket";
import { TOUR_EXPAND_MENU_EVENT } from "../../../config/onboardingTourConfig";
import { isFranchisePortalSession } from "../../../utils/franchiseSession";
import {
  computeDisabledMenuKeysForUser,
  getStoredIsOwner,
  getStoredUserPermissions,
} from "../../../utils/userPermissions";
import {
  clearRestaurantSession,
  forceLogoutToLogin,
} from "../../../utils/sessionAuth";
import {
  decodeTokenPayload,
  getAuthToken,
} from "../../../utils/authToken";
import { LOGOUT_ACTION } from "../../../store/actions/AuthActions";
import { useDispatch } from "react-redux";
import { usePlanAccess } from "../../components/Plans/PlanAccess";
import { isPlanLockedMenuKey, promptBuyPlan } from "../../../utils/planLock";
import "./sidebarLuxe.css";

const reducer = (previousState, updatedState) => ({
  ...previousState,
  ...updatedState,
});

const initialState = {
  active: "",
  activeSubmenu: "",
};

const isIconRailSidebar = () => {
  if (typeof document === "undefined") return false;
  const style = document.body.getAttribute("data-sidebar-style");
  if (style === "mini") return true;
  if (style === "full") {
    return Boolean(
      document.getElementById("main-wrapper")?.classList.contains("menu-toggle")
    );
  }
  return false;
};

const SideBar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { planExpired } = usePlanAccess();
  var d = new Date();
  const [addMenus, setAddMenus] = useState(false);
  const [state, setState] = useReducer(reducer, initialState);
  const [callsBadge, setCallsBadge] = useState(0);
  const [iconRail, setIconRail] = useState(isIconRailSidebar);
  const [flyoutKey, setFlyoutKey] = useState("");
  const flyoutTimer = useRef(null);
  const [disabledMenuKeys, setDisabledMenuKeys] = useState([]);
  const franchisePortal = isFranchisePortalSession();

  const [userDisabledKeys, setUserDisabledKeys] = useState(() => {
    if (franchisePortal) return [];
    const isOwner = getStoredIsOwner();
    const perms = getStoredUserPermissions();
    return computeDisabledMenuKeysForUser(perms, isOwner);
  });

  const combinedDisabledKeys = useMemo(() => {
    if (franchisePortal) return [];
    return withLinkedArchiveKeys(
      Array.from(new Set([...disabledMenuKeys, ...userDisabledKeys]))
    );
  }, [franchisePortal, disabledMenuKeys, userDisabledKeys]);

  const MenuList = filterMenuListByDisabledKeys(
    franchisePortal ? getFranchiseMenuList(t) : getMenuList(t),
    franchisePortal ? [] : combinedDisabledKeys
  );

  useEffect(() => {
    if (franchisePortal) return undefined;

    const loadSidebarAccess = async () => {
      try {
        const result = await fetchBackofficeSidebarSettings();
        const keys = result?.disabledBackofficeMenuKeys;
        setDisabledMenuKeys(Array.isArray(keys) ? keys : []);
      } catch (error) {
        console.error("Error loading sidebar access:", error);
      }
    };

    const loadUserPermissions = async () => {
      try {
        const user = await fetchConnectedUser();
        if (user) {
          localStorage.setItem(
            "isOwner",
            JSON.stringify(Boolean(user.isOwner))
          );
          if (Array.isArray(user.permissions)) {
            localStorage.setItem(
              "userPermissions",
              JSON.stringify(user.permissions)
            );
          }
          setUserDisabledKeys(
            computeDisabledMenuKeysForUser(user.permissions, user.isOwner)
          );
        }
      } catch (error) {
        console.error("Error loading user permissions in SideBar:", error);
      }
    };

    loadSidebarAccess();
    loadUserPermissions();
  }, [franchisePortal]);

  // Fetch pending calls count and listen for real-time updates
  useEffect(() => {
    if (franchisePortal) return undefined;

    const setupSocket = async () => {
      try {
        // Wait for socket to be connected
        if (!socket.connected) {
          socket.connect();
          await new Promise((resolve) => {
            if (socket.connected) {
              resolve();
            } else {
              socket.once("connect", resolve);
            }
          });
        }

        const user = await fetchConnectedUser();
        joinRestaurantRoom(user?.restaurantFK);
      } catch (error) {
        console.error("Error setting up socket in SideBar:", error);
      }
    };

    const fetchCallsCount = async () => {
      try {
        const response = await getCallsByRestaurant();
        setCallsBadge(response.pending || 0);
      } catch (error) {
        console.error("Error fetching calls count:", error);
      }
    };

    // Initial setup
    setupSocket();
    fetchCallsCount();

    // Listen for calls count updates from Calls component
    const handleCallsCountUpdated = (event) => {
      const { pending } = event.detail;
      console.log(`📊 SideBar badge updated from Calls component: ${pending}`);
      setCallsBadge(pending);
    };

    // Listen for new server calls in real-time
    const handleServerCall = (data) => {
      console.log("📞 New call received in SideBar, updating badge count:", data);
      // Update badge count directly without refetching
      setCallsBadge((prev) => {
        const newCount = prev + 1;
        console.log(`📊 SideBar badge count updated: ${prev} → ${newCount}`);
        return newCount;
      });
    };

    // Listen for call acknowledgments (when admin clicks "Server On The Way")
    const handleCallAcknowledged = () => {
      console.log("✅ Call acknowledged in SideBar, decreasing badge count");
      // Decrease badge count when call is acknowledged
      setCallsBadge((prev) => {
        const newCount = Math.max(0, prev - 1);
        console.log(`📊 SideBar badge count decreased: ${prev} → ${newCount}`);
        return newCount;
      });
    };

    const handleForceLogout = (payload = {}) => {
      if (payload?.userId) {
        const decoded = decodeTokenPayload(getAuthToken());
        const currentId =
          decoded?.id ||
          decoded?.user?.id ||
          decoded?.user?._id ||
          decoded?._id;
        if (currentId && String(payload.userId) !== String(currentId)) {
          return;
        }
      }

      clearRestaurantSession();
      dispatch({ type: LOGOUT_ACTION });
      forceLogoutToLogin();
    };

    // Listen for socket connection events
    const handleConnect = () => {
      setupSocket();
    };

    const handleDisconnect = () => {
      console.log("❌ Socket disconnected in SideBar");
    };

    // Listen to custom event from Calls component
    window.addEventListener("calls-count-updated", handleCallsCountUpdated);

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("server-call", handleServerCall);
    socket.on("call-acknowledged", handleCallAcknowledged);
    socket.on("force-logout", handleForceLogout);

    return () => {
      window.removeEventListener("calls-count-updated", handleCallsCountUpdated);
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("server-call", handleServerCall);
      socket.off("call-acknowledged", handleCallAcknowledged);
      socket.off("force-logout", handleForceLogout);
    };
  }, [franchisePortal, dispatch]);

  let handleheartBlast = document.querySelector(".heart");
  function heartBlast() {
    return handleheartBlast.classList.toggle("heart-blast");
  }

  const [hideOnScroll, setHideOnScroll] = useState(true);
  useScrollPosition(
    ({ prevPos, currPos }) => {
      const isShow = currPos.y > prevPos.y;
      if (isShow !== hideOnScroll) setHideOnScroll(isShow);
    },
    [hideOnScroll]
  );

  const stopIfLocked = (menuKey, event) => {
    if (!planExpired || !isPlanLockedMenuKey(menuKey)) return false;
    event.preventDefault();
    promptBuyPlan(t, navigate);
    return true;
  };

  useEffect(() => {
    const syncIconRail = () => setIconRail(isIconRailSidebar());
    syncIconRail();
    window.addEventListener("resize", syncIconRail);
    const wrapper = document.getElementById("main-wrapper");
    const observer = new MutationObserver(syncIconRail);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["data-sidebar-style"],
    });
    if (wrapper) {
      observer.observe(wrapper, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }
    return () => {
      window.removeEventListener("resize", syncIconRail);
      observer.disconnect();
      if (flyoutTimer.current) window.clearTimeout(flyoutTimer.current);
    };
  }, []);

  const openFlyout = useCallback((menuKey) => {
    if (flyoutTimer.current) {
      window.clearTimeout(flyoutTimer.current);
      flyoutTimer.current = null;
    }
    setFlyoutKey(menuKey);
  }, []);

  const closeFlyout = useCallback(() => {
    if (flyoutTimer.current) window.clearTimeout(flyoutTimer.current);
    flyoutTimer.current = window.setTimeout(() => {
      setFlyoutKey("");
      flyoutTimer.current = null;
    }, 220);
  }, []);

  const handleMenuActive = (status) => {
    setState({ active: status });
    if (state.active === status) {
      setState({ active: "" });
    }
  };
  const handleSubmenuActive = (status) => {
    setState({ activeSubmenu: status });
    if (state.activeSubmenu === status) {
      setState({ activeSubmenu: "" });
    }
  };

  /// Path
  let path = window.location.pathname;
  path = path.split("/");
  path = path[path.length - 1];
  useEffect(() => {
    let matchedActive = null;
    let matchedSubmenu = null;
    MenuList.forEach((parent) => {
      parent.content?.forEach((item) => {
        if (path === item.to) {
          matchedActive = parent.menuKey;
        }
        item.content?.forEach((ele) => {
          if (path === ele.to) {
            matchedSubmenu = item.menuKey;
            matchedActive = parent.menuKey;
          }
        });
      });
    });
    // Only open the matching group — do not collapse open menus on leaf routes
    // (e.g. dashboard), or the welcome tour cannot highlight submenu items.
    if (matchedActive) {
      setState({
        active: matchedActive,
        ...(matchedSubmenu ? { activeSubmenu: matchedSubmenu } : {}),
      });
    }
  }, [path]);

  useEffect(() => {
    const handleExpandMenu = (event) => {
      const menuKey = event?.detail?.menuKey;
      if (menuKey) {
        setState({ active: menuKey });
      }
    };

    window.addEventListener(TOUR_EXPAND_MENU_EVENT, handleExpandMenu);
    return () => window.removeEventListener(TOUR_EXPAND_MENU_EVENT, handleExpandMenu);
  }, []);

  return (
    <div className="deznav">
      <div className="deznav-scroll dz-scroll">
        <div className="sidebar-start-now-wrap">
          <Link
            to="/start-now"
            className={`sidebar-start-now-btn${planExpired ? " is-plan-locked" : ""}`}
            title={t("sidebarStartNow")}
            data-tour="tour-nav-start-now"
            onClick={(event) => {
              if (planExpired) stopIfLocked("kitchen", event);
            }}
          >
            <i className="fas fa-rocket sidebar-start-now-btn__icon" aria-hidden="true" />
            <span className="sidebar-start-now-btn__text">{t("sidebarStartNow")}</span>
          </Link>
        </div>
        <ul className="metismenu" id="menu">
          {MenuList.map((data, index) => {
            let menuClass = data.classsChange;
            if (menuClass === "menu-title") {
              return (
                <li className={menuClass} key={index}>
                  {data.title}
                </li>
              );
            } else {
              return (
                <Fragment key={index}>
                  <li
                    className={` ${
                      state.active === data.menuKey ? "mm-active" : ""
                    } ${
                      (!data.content || data.content.length === 0) &&
                      data.to === path
                        ? "mm-active"
                        : ""
                    }${
                      data.content && data.content.length > 0
                        ? " has-flyout"
                        : ""
                    }${
                      flyoutKey === data.menuKey ? " is-flyout-open" : ""
                    }${
                      planExpired && isPlanLockedMenuKey(data.menuKey)
                        ? " is-plan-locked"
                        : ""
                    }`}
                    key={index}
                    onMouseEnter={() => {
                      if (data.content && data.content.length > 0) {
                        openFlyout(data.menuKey);
                      }
                    }}
                    onMouseLeave={() => {
                      if (data.content && data.content.length > 0) {
                        closeFlyout();
                      }
                    }}
                  >
                    {data.content && data.content.length > 0 ? (
                      <Fragment>
                        <Link
                          to={data.to || "#"}
                          className={`has-arrow${
                            planExpired && isPlanLockedMenuKey(data.menuKey) ? " is-plan-locked" : ""
                          }`}
                          data-tour={`tour-nav-${data.menuKey}`}
                          onClick={(e) => {
                            if (stopIfLocked(data.menuKey, e)) return;
                            if (iconRail) {
                              e.preventDefault();
                              openFlyout(data.menuKey);
                              return;
                            }
                            const rect = e.currentTarget.getBoundingClientRect();
                            const clickedArrow = rect.right - e.clientX <= 44;
                            if (clickedArrow) {
                              e.preventDefault();
                              handleMenuActive(data.menuKey);
                              return;
                            }
                            if (data.to) {
                              setState({ active: data.menuKey });
                            } else {
                              handleMenuActive(data.menuKey);
                            }
                          }}
                        >
                          {data.iconStyle}{" "}
                          <span className="nav-text">{data.title}</span>
                          {planExpired && isPlanLockedMenuKey(data.menuKey) ? (
                            <i className="fas fa-lock sidebar-plan-lock" aria-hidden="true" />
                          ) : null}
                        </Link>
                        <Collapse in={iconRail || state.active === data.menuKey}>
                          <ul
                            className={`${
                              menuClass === "mm-collapse" ? "mm-show" : ""
                            }`}
                          >
                            {data.content &&
                              data.content.map((subItem, subIndex) => {
                                return (
                                  <li
                                    key={subIndex}
                                    className={`${
                                      state.activeSubmenu === subItem.menuKey
                                        ? "mm-active"
                                        : ""
                                    } ${subItem.to === path ? "mm-active" : ""}`}
                                  >
                                    {subItem.content &&
                                    subItem.content.length > 0 ? (
                                      <>
                                        <Link
                                          to={subItem.to}
                                          className={
                                            subItem.hasMenu ? "has-arrow" : ""
                                          }
                                          onClick={(event) => {
                                            if (stopIfLocked(subItem.menuKey, event)) return;
                                            handleSubmenuActive(subItem.menuKey);
                                          }}
                                        >
                                          {subItem.title}
                                          {planExpired && isPlanLockedMenuKey(subItem.menuKey) ? (
                                            <i className="fas fa-lock sidebar-plan-lock" aria-hidden="true" />
                                          ) : null}
                                          {subItem.badge === "callsBadge" &&
                                            callsBadge > 0 && (
                                              <Badge
                                                bg="danger"
                                                className="ms-2"
                                                style={{ fontSize: "10px" }}
                                              >
                                                {callsBadge}
                                              </Badge>
                                            )}
                                        </Link>
                                        <Collapse
                                          in={
                                            state.activeSubmenu === subItem.menuKey
                                              ? true
                                              : false
                                          }
                                        >
                                          <ul
                                            className={`${
                                              menuClass === "mm-collapse"
                                                ? "mm-show"
                                                : ""
                                            }`}
                                          >
                                            {subItem.content &&
                                              subItem.content.map((nested, nestedIndex) => {
                                                return (
                                                  <Fragment key={nestedIndex}>
                                                    <li>
                                                      <Link
                                                        className={`${
                                                          path === nested.to
                                                            ? "mm-active"
                                                            : ""
                                                        }`}
                                                        to={nested.to}
                                                      >
                                                        {nested.title}
                                                      </Link>
                                                    </li>
                                                  </Fragment>
                                                );
                                              })}
                                          </ul>
                                        </Collapse>
                                      </>
                                    ) : (
                                      <Link
                                        to={
                                          subItem.search
                                            ? {
                                                pathname: subItem.to,
                                                search: subItem.search,
                                              }
                                            : subItem.to
                                        }
                                        className={`${
                                          subItem.to === path ? "mm-active" : ""
                                        }${
                                          planExpired && isPlanLockedMenuKey(subItem.menuKey)
                                            ? " is-plan-locked"
                                            : ""
                                        }`}
                                        data-tour={`tour-nav-${subItem.menuKey}`}
                                        onClick={(event) => {
                                          stopIfLocked(subItem.menuKey, event);
                                        }}
                                      >
                                        {subItem.title}
                                        {planExpired && isPlanLockedMenuKey(subItem.menuKey) ? (
                                          <i className="fas fa-lock sidebar-plan-lock" aria-hidden="true" />
                                        ) : null}
                                      </Link>
                                    )}
                                  </li>
                                );
                              })}
                          </ul>
                        </Collapse>
                      </Fragment>
                    ) : (
                      <Link
                        to={
                          data.search
                            ? { pathname: data.to, search: data.search }
                            : data.to
                        }
                        className={`${data.to === path ? "mm-active" : ""}${
                          planExpired && isPlanLockedMenuKey(data.menuKey) ? " is-plan-locked" : ""
                        }`}
                        data-tour={`tour-nav-${data.menuKey}`}
                        onClick={(event) => {
                          stopIfLocked(data.menuKey, event);
                        }}
                      >
                        {data.iconStyle}
                        <span className="nav-text">{data.title}</span>
                        {planExpired && isPlanLockedMenuKey(data.menuKey) ? (
                          <i className="fas fa-lock sidebar-plan-lock" aria-hidden="true" />
                        ) : null}
                        {data.badge === "callsBadge" && callsBadge > 0 && (
                          <Badge
                            bg="danger"
                            className="ms-2"
                            style={{ fontSize: "10px" }}
                          >
                            {callsBadge}
                          </Badge>
                        )}
                      </Link>
                    )}
                  </li>

                  {/* (open restaurant button moved to navbar) */}
                </Fragment>
              );
            }
          })}
        </ul>
      </div>
    </div>
  );
};

export default SideBar;
